import { memo, useCallback, useMemo } from 'react';
import { CircularProgress, FormControl } from '@material-ui/core';
import cx from 'classnames';
import { camelCase } from 'lodash';
import PropTypes from 'prop-types';

import CustomInput from 'src/components/CustomInput';
import { CustomTypography, TYPOGRAPHY_VARIANT } from 'src/components/CustomTypography';
import { FieldLabel } from 'src/components/FieldLabel';
import TextCounter from 'src/components/TextCounter';

import { useStyles } from './useStyles';
import { prepareError } from './utils';

const BriefTextField = ({
  className,
  classes,
  label,
  subtitle,
  error,
  placeholder,
  value,
  onChange,
  hint,
  disabled,
  InputProps,
  charCounter,
  wordCounter,
  displayCharCounter,
  displayWordCounter,
  processing,
  words,
  fieldLabelClasses,
  rows,
  rowsMin,
  rowsMax,
  isOptional,
  isQuestion,
  hideErrorMessage,
  disableAutofillStyle,
  helperText,
  dataTestId,
  titleBold,
  multiline,
  endAdornmentText,
  counterPosition,
  ...restProps
}) => {
  const styles = useStyles({ classes });

  const { hasError, errorMessage } = prepareError(error);

  const displayCounter = displayCharCounter || displayWordCounter;
  const counterValue = displayCharCounter ? charCounter?.value || 0 : wordCounter?.value || 0;
  const counterMin = displayCharCounter ? charCounter?.min || 0 : wordCounter?.min || 0;
  const counterMax = displayCharCounter ? charCounter?.max || 0 : wordCounter?.max || 0;
  const counterStrict = displayCharCounter ? charCounter?.strict : wordCounter?.strict || false;
  const endAdornmentTextElement = endAdornmentText ? (
    <span className={styles.endAdornmentText}>{endAdornmentText}</span>
  ) : undefined;
  const inputClasses = useMemo(
    () => ({
      root: cx(styles.input_root, { [styles.disableAutofillStyle]: disableAutofillStyle }),
      inputMultiline: styles.inputMultiline,
      disabled: styles.disabled,
    }),
    [styles, disableAutofillStyle]
  );

  const handleChange = useCallback(
    (event) => {
      onChange(event.target.value);
    },
    [onChange]
  );

  return (
    <FormControl
      className={cx(styles.root, className)}
      {...restProps}
    >
      <div
        className={cx(styles.header, {
          [styles.empty]: !label && !subtitle && (!displayCounter || counterPosition === 'bottom'),
        })}
      >
        <FieldLabel
          className={titleBold ? styles.labelBold : styles.label}
          label={label}
          hint={hint}
          isOptional={isOptional}
          isQuestion={isQuestion}
          classes={fieldLabelClasses}
        />
        {subtitle && (
          <CustomTypography
            className={styles.subtitle}
            variant={TYPOGRAPHY_VARIANT.regular12}
          >
            {subtitle}
          </CustomTypography>
        )}
        {words && (
          <CustomTypography
            className={styles.words}
            variant={TYPOGRAPHY_VARIANT.regular12}
          >
            {words}
          </CustomTypography>
        )}
        {displayCounter && counterPosition === 'top' && (
          <TextCounter
            className={styles.textCounter}
            count={counterValue}
            minCount={counterMin}
            maxCount={counterMax}
            strict={counterStrict}
            suffix={displayWordCounter ? 'words' : ''}
          />
        )}
      </div>
      <div className={styles.inputWrapper}>
        <CustomInput
          classes={inputClasses}
          data-testid="contentTextField"
          value={value || ''}
          onChange={handleChange}
          placeholder={placeholder}
          disabled={disabled}
          error={hasError}
          rows={rows}
          rowsMin={rowsMin}
          rowsMax={rowsMax}
          multiline={multiline}
          dataTestId={dataTestId || label ? camelCase(label) : undefined}
          endAdornment={
            processing ? (
              <div className={styles.processing}>
                <CircularProgress size={24} />
              </div>
            ) : (
              endAdornmentTextElement
            )
          }
          {...InputProps}
        />
      </div>
      {displayCounter && counterPosition === 'bottom' && (
        <TextCounter
          className={styles.bottomTextCounter}
          count={counterValue}
          minCount={counterMin}
          maxCount={counterMax}
          strict={counterStrict}
          suffix={displayWordCounter ? 'words' : ''}
        />
      )}

      {helperText && (
        <CustomTypography
          className={styles.helperText}
          variant={TYPOGRAPHY_VARIANT.regular12}
        >
          {helperText}
        </CustomTypography>
      )}

      {errorMessage && !hideErrorMessage && (
        <CustomTypography
          className={styles.error}
          variant={TYPOGRAPHY_VARIANT.regular12}
          color="error"
        >
          {errorMessage}
        </CustomTypography>
      )}
    </FormControl>
  );
};

BriefTextField.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.shape({}),
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  subtitle: PropTypes.string,
  error: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.bool,
  ]),
  placeholder: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  hint: PropTypes.string,
  disabled: PropTypes.bool,
  InputProps: PropTypes.shape({}),
  charCounter: PropTypes.shape(),
  wordCounter: PropTypes.shape(),
  displayCharCounter: PropTypes.bool,
  displayWordCounter: PropTypes.bool,
  processing: PropTypes.bool,
  words: PropTypes.string,
  rows: PropTypes.number,
  rowsMin: PropTypes.number,
  rowsMax: PropTypes.number,
  fieldLabelClasses: PropTypes.shape({}),
  isOptional: PropTypes.bool,
  isQuestion: PropTypes.bool,
  hideErrorMessage: PropTypes.bool,
  disableAutofillStyle: PropTypes.bool,
  titleBold: PropTypes.bool,
  helperText: PropTypes.string,
  dataTestId: PropTypes.string,
  multiline: PropTypes.bool,
  endAdornmentText: PropTypes.string,
  counterPosition: PropTypes.oneOf(['top', 'bottom']),
};

BriefTextField.defaultProps = {
  className: null,
  classes: null,
  fieldLabelClasses: null,
  label: '',
  subtitle: '',
  error: null,
  placeholder: '',
  value: '',
  hint: null,
  disabled: false,
  InputProps: null,
  charCounter: null,
  wordCounter: null,
  displayCharCounter: false,
  displayWordCounter: false,
  processing: false,
  words: '',
  rows: 1,
  rowsMin: 1,
  rowsMax: 5,
  isOptional: false,
  isQuestion: false,
  hideErrorMessage: false,
  disableAutofillStyle: false,
  titleBold: false,
  helperText: '',
  dataTestId: '',
  multiline: true,
  endAdornmentText: null,
  counterPosition: 'top',
};

export default memo(BriefTextField);
